import React from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import { msg } from 'api';
import { getProductImageUrl, IMAGE_SIZES } from 'utils/productImages';
import { getProductDetailsLink } from 'apps/products/links';
import { getMetaTitle, getMetaDescription } from 'apps/meta/utils';

const getKeywordsFromTags = (tags = {}) => {
  const keywords = new Set();

  const groups = tags.groups || {};
  const categories = tags.categories || [];

  // take keywords from groups
  Object.values(groups).map(
    group => group
      .map(item => item.name)
      .forEach(keyword => keywords.add(keyword))
  );

  // take keywords from categories
  categories
    .filter(category => category.tags && category.tags.length)
    .map(category => category.tags[category.tags.length - 1].name)
    .forEach(keyword => keywords.add(keyword));

  return Array.from(keywords);
};

const getTitle = ({ backOfPack, pageMetaData, product }) => {
  const scribeTitle = backOfPack.seo && backOfPack.seo.title;
  if (scribeTitle) {
    return scribeTitle;
  }

  const title = getMetaTitle(pageMetaData, {
    productName: product.name,
    titleSuffix: msg.page.titleSuffix,
  });

  return title || `${product.name}${msg.page.titleSuffix}`;
};

const getDescription = ({ backOfPack, pageMetaData, product }) => {
  const scribeDescription = backOfPack.seo && backOfPack.seo.description;
  if (scribeDescription) {
    return scribeDescription;
  }

  const brandName = product.brand && product.brand.name;
  const description = getMetaDescription(pageMetaData, {
    brand: brandName,
    productName: product.name,
    titleSuffix: msg.page.titleSuffix,
  });

  return description || `Buy ${product.name} from ${msg.retailer}. ${msg.product.bopDescription}`;
};

const Metadata = ({ product, backOfPack, pageMetaData = {} }) => {
  const keywords = msg.product.bopStandardKeywords.split(', ')
    .concat(getKeywordsFromTags(backOfPack.tags))
    .concat(backOfPack.locationDescriptionList);
  const title = getTitle({ backOfPack, pageMetaData, product });
  const description = getDescription({ backOfPack, pageMetaData, product });

  const content = new Set(keywords);
  const imageUrl = getProductImageUrl(product.sku, product.image, IMAGE_SIZES.S_268);
  const canonicalLink = product && getProductDetailsLink({ product, backOfPack });

  return (
    <Helmet
      title={title}
      meta={[{
        name: 'description',
        content: description,
      }, {
        name: 'keywords',
        content: Array.from(content)
          .join(', '),
      },
      {
        property: 'og:title',
        content: title,
      }, {
        property: 'og:type',
        content: 'product',
      }, {
        property: 'og:image',
        content: imageUrl,
      }, {
        property: 'og:site_name',
        content: msg.page.title,
      }, {
        property: 'og:description',
        content: description,
      },
      {
        property: 'og:url',
        content: canonicalLink,
      }]}
    />
  );
};
Metadata.defaultProps = {
  product: {
    brand: {},
    name: {},
  },
  backOfPack: {
    tags: {
      categories: {},
      groups: {},
    },
  },
};

Metadata.propTypes = {
  product: PropTypes.shape({
    brand: PropTypes.shape({ name: PropTypes.string.isRequired }),
    name: PropTypes.string.isRequired,
  }),
  backOfPack: PropTypes.shape({
    tags: PropTypes.shape({
      categories: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
      groups: PropTypes.object,
    }),
    locationDescription: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default Metadata;
